import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useToastStore, type Toast as ToastType } from '../../stores/toast.store';
import { cn } from '../../lib/utils';

const CONFIG = {
  success: { border: 'border-cyber-green/30', bg: 'bg-cyber-green-dim', titleColor: 'text-cyber-green', dot: 'bg-cyber-green' },
  error:   { border: 'border-cyber-red/30',   bg: 'bg-cyber-red-dim',   titleColor: 'text-cyber-red',   dot: 'bg-cyber-red' },
  warning: { border: 'border-cyber-yellow/30', bg: 'bg-cyber-yellow-dim', titleColor: 'text-cyber-yellow', dot: 'bg-cyber-yellow' },
  info:    { border: 'border-brand-500/30',    bg: 'bg-brand-950',        titleColor: 'text-brand-400',    dot: 'bg-brand-400' },
};

function ToastItem({ toast }: { toast: ToastType }) {
  const { remove } = useToastStore();
  const [visible, setVisible] = useState(false);
  const cfg = CONFIG[toast.type];

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => remove(toast.id), 150);
  };

  return (
    <div
      className={cn(
        'panel border rounded-lg min-w-72 max-w-sm shadow-2xl',
        cfg.border, cfg.bg,
        'transition-all duration-150',
        visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6',
      )}
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <div className={cn('w-2 h-2 rounded-full shrink-0 mt-1', cfg.dot)} />
        <div className="flex-1 min-w-0">
          <p className={cn('text-sm font-medium leading-snug', cfg.titleColor)}>
            {toast.title}
          </p>
          {toast.message && (
            <p className="text-xs text-ink-mid mt-0.5 leading-snug">{toast.message}</p>
          )}
        </div>
        <button
          onClick={handleClose}
          className="shrink-0 text-ink-dim hover:text-ink-full transition-colors rounded p-0.5"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

export function ToastContainer() {
  const { toasts } = useToastStore();

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col gap-2 pointer-events-none" role="region" aria-live="polite" aria-label="Notifications">
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto" role="alert">
          <ToastItem toast={toast} />
        </div>
      ))}
    </div>
  );
}
